/**
 * LL Blossoms — Scroll Reveal Animations
 * Intersection Observer-based reveal for sections and staggered cards
 */
(function () {
  'use strict';

  const EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';
  const DURATION = '0.75s';
  const THRESHOLD = 0.12;

  function initReveal() {
    // Reveal whole sections (except header + announcement bar)
    const sections = document.querySelectorAll(
      '.shopify-section:not(.shopify-section-group-header-group):not([class*="announcement"])'
    );

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('llbny-visible');
          sectionObserver.unobserve(entry.target);
        }
      });
    }, { threshold: THRESHOLD });

    sections.forEach((el) => {
      // Skip hero — it's already in view on load
      if (el.querySelector('.hero-video')) return;
      el.classList.add('llbny-reveal');
      sectionObserver.observe(el);
    });

    // Framer-style spring stagger for treatments grid.
    // Observe the SECTION, not the list — after clones are injected the list
    // is ~14 cards wide, so only ~2% of it is ever in viewport, which is
    // below any reasonable threshold and the observer would never fire.
    const txGrid = document.querySelector('[id*="treatments_grid"] .multicolumn-list');
    const txSection = document.querySelector('[id*="treatments_grid"]');
    if (txGrid && txSection) {
      const COLS = 4;
      // Capture real items only (clones don't exist yet at this point)
      const txItems = Array.from(txGrid.querySelectorAll('.multicolumn-list__item'));
      const txObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            txItems.forEach((card, i) => {
              card.style.transitionDelay = `${i * 80}ms`;
              card.classList.add('llbny-tx-in');
            });
            txObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });
      txObserver.observe(txSection);
    }

    // Stagger cards inside other multicolumn sections and blog (skip treatments grid, handled above)
    const allCardLists = document.querySelectorAll('.multicolumn-list, .blog-articles');
    const cardLists = Array.from(allCardLists).filter((el) => el !== txGrid);

    const cardObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const cards = entry.target.querySelectorAll(
            '.multicolumn-list__item, .article'
          );
          cards.forEach((card, i) => {
            card.style.transitionDelay = `${i * 80}ms`;
            card.classList.add('llbny-card-reveal', 'llbny-card-visible');
          });
          cardObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.05 });

    cardLists.forEach((el) => {
      const cards = el.querySelectorAll('.multicolumn-list__item, .article');
      cards.forEach((card) => card.classList.add('llbny-card-reveal'));
      cardObserver.observe(el);
    });
  }

  function initTransparentHeader() {
    if (!document.body.classList.contains('template-index')) return;
    const wrapper = document.querySelector('.header-wrapper');
    if (!wrapper) return;

    // Measure actual header height and set the CSS offset so hero bleeds behind it
    const setOffset = () => {
      const h = wrapper.offsetHeight;
      if (h > 0) {
        document.documentElement.style.setProperty('--llbny-header-offset', `-${h}px`);
      }
    };
    setOffset();
    // Re-measure after fonts/images load in case height changes
    window.addEventListener('load', setOffset);

    const onScroll = () => {
      const annBar = document.querySelector('.announcement-bar__wrap');
      const threshold = (annBar ? annBar.offsetHeight : 0) + 10;
      wrapper.classList.toggle('llbny-header-scrolled', window.scrollY > threshold);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function initTreatmentsCarousel() {
    var section = document.querySelector('[id*="treatments_grid"]');
    if (!section) return;
    var sliderGutter = section.querySelector('.slider-mobile-gutter');
    var list = section.querySelector('.multicolumn-list');
    if (!list || !sliderGutter) return;
    var items = Array.from(list.querySelectorAll('.multicolumn-list__item'));
    if (items.length < 5) return;

    // Wrap slider-gutter in a positioned outer div for arrow placement
    var outer = document.createElement('div');
    outer.className = 'llbny-tx-outer';
    sliderGutter.parentNode.insertBefore(outer, sliderGutter);
    outer.appendChild(sliderGutter);
    sliderGutter.classList.add('llbny-tx-overflow');

    // Prev / next arrow buttons
    var chevL = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';
    var chevR = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';
    var prevBtn = document.createElement('button');
    prevBtn.className = 'llbny-tx-btn llbny-tx-prev';
    prevBtn.setAttribute('aria-label', 'Previous treatments');
    prevBtn.innerHTML = chevL;
    var nextBtn = document.createElement('button');
    nextBtn.className = 'llbny-tx-btn llbny-tx-next';
    nextBtn.setAttribute('aria-label', 'Next treatments');
    nextBtn.innerHTML = chevR;
    outer.appendChild(prevBtn);
    outer.appendChild(nextBtn);

    // Dots container (placed after outer)
    var dotsEl = document.createElement('div');
    dotsEl.className = 'llbny-tx-dots';
    outer.parentNode.insertBefore(dotsEl, outer.nextSibling);

    var current = 0;
    var dots = [];
    var cloneCount = 0; // clones prepended before real items

    function getVisibleCount() {
      if (window.innerWidth >= 990) return 4;
      if (window.innerWidth >= 750) return 2;
      return 1;
    }

    function allDOMItems() {
      return Array.from(list.querySelectorAll('.multicolumn-list__item'));
    }

    // ── Clone setup for infinite loop ──
    function setupClones() {
      Array.from(list.querySelectorAll('.llbny-tx-clone')).forEach(function (c) { c.remove(); });
      var vc = Math.ceil(getVisibleCount());
      cloneCount = vc;
      // Prepend clones of last vc items (for backward wrap)
      for (var i = items.length - vc; i < items.length; i++) {
        var c = items[i].cloneNode(true);
        c.classList.add('llbny-tx-clone');
        c.removeAttribute('id');
        c.setAttribute('aria-hidden', 'true');
        list.insertBefore(c, items[0]);
      }
      // Append clones of first vc items (for forward wrap)
      for (var i = 0; i < vc; i++) {
        var c = items[i].cloneNode(true);
        c.classList.add('llbny-tx-clone');
        c.removeAttribute('id');
        c.setAttribute('aria-hidden', 'true');
        list.appendChild(c);
      }
    }

    function buildDots(visCount) {
      dotsEl.innerHTML = '';
      dots = [];
      var maxSlide = items.length - Math.ceil(visCount);
      for (var i = 0; i <= maxSlide; i++) {
        var d = document.createElement('button');
        d.className = 'llbny-tx-dot' + (i === 0 ? ' active' : '');
        d.setAttribute('aria-label', 'Go to treatment ' + (i + 1));
        d.dataset.index = i;
        dotsEl.appendChild(d);
        dots.push(d);
      }
    }

    function setLayout(visCount) {
      var containerW = sliderGutter.getBoundingClientRect().width;
      var gapStr = getComputedStyle(list).columnGap || getComputedStyle(list).gap || '10px';
      var gapPx = parseFloat(gapStr) || 10;
      var gapsInView = Math.ceil(visCount) - 1;
      var cardW = (containerW - gapsInView * gapPx) / visCount;
      // Apply to all items including clones
      allDOMItems().forEach(function (item) {
        item.style.flex = '0 0 ' + cardW + 'px';
        item.style.maxWidth = cardW + 'px';
      });
    }

    function updateDots(idx) {
      var vc = Math.ceil(getVisibleCount());
      var maxSlide = items.length - vc;
      var norm = ((idx % items.length) + items.length) % items.length;
      norm = Math.min(norm, maxSlide);
      dots.forEach(function (d, i) { d.classList.toggle('active', i === norm); });
    }

    function offsetForIndex(idx) {
      var dom = allDOMItems();
      var el = dom[idx + cloneCount];
      return el ? el.offsetLeft : 0;
    }

    function applyTransform(offset) {
      list.style.setProperty('transform', 'translateX(-' + offset + 'px)', 'important');
    }

    function goTo(idx) {
      if (window.innerWidth < 750) {
        var vc = getVisibleCount();
        var maxSlide = items.length - Math.ceil(vc);
        current = Math.max(0, Math.min(idx, maxSlide));
        sliderGutter.scrollLeft = items[current].offsetLeft;
        updateDots(current);
        return;
      }
      var vc = Math.ceil(getVisibleCount());
      var maxSlide = items.length - vc;
      if (idx > maxSlide) {
        // Animate into the appended clones, which mirror slide 0 exactly
        current = items.length;
        updateDots(0);
      } else if (idx < 0) {
        // Animate into the prepended clones, which mirror slide maxSlide exactly
        current = -cloneCount;
        updateDots(maxSlide);
      } else {
        current = idx;
        updateDots(idx);
      }
      applyTransform(offsetForIndex(current));
      // Schedule a fallback snap in case transitionend doesn't fire
      // (e.g. animation interrupted, or browser quirk)
      if (current === items.length || current === -cloneCount) {
        clearTimeout(snapFallback);
        snapFallback = setTimeout(snapToReal, 700);
      }
    }

    // ── Snap helper ──────────────────────────────────────────────────────────
    // Called after the carousel finishes animating into clone territory.
    // Must be invisible: disable transition → move → force the browser to
    // commit the transform's computed value (getComputedStyle, NOT
    // getBoundingClientRect — transforms don't affect layout so a layout flush
    // doesn't resolve transition state) → re-enable transition next frame.
    function snapToReal() {
      var vc = Math.ceil(getVisibleCount());
      var maxSlide = items.length - vc;
      if (current >= 0 && current <= maxSlide) return; // nothing to snap
      current = ((current % items.length) + items.length) % items.length;
      if (current > maxSlide) current = 0;
      list.style.setProperty('transition', 'none', 'important');
      applyTransform(offsetForIndex(current));
      // Reading getComputedStyle().transform forces the browser to resolve the
      // transition computed value, making this position the new animation baseline.
      // Without this read, re-enabling the transition can animate FROM the old
      // clone position TO the real position — the visible rubberband.
      void getComputedStyle(list).transform;
      requestAnimationFrame(function () {
        list.style.removeProperty('transition');
        updateDots(current);
      });
    }

    // transitionend: guard against child-card events bubbling up.
    // Card reveal animations also transition 'transform', and bubbled events
    // would prematurely fire snapToReal mid-carousel-animation.
    var snapFallback;
    list.addEventListener('transitionend', function (e) {
      if (e.target !== list || e.propertyName !== 'transform' || window.innerWidth < 750) return;
      clearTimeout(snapFallback);
      snapFallback = null;
      snapToReal();
    });

    function reset() {
      var visCount = getVisibleCount();
      setupClones();
      setLayout(visCount);
      if (window.innerWidth < 750) {
        list.style.removeProperty('transform');
        dotsEl.style.display = 'none';
      } else {
        dotsEl.style.display = '';
        buildDots(visCount);
        var maxSlide = items.length - Math.ceil(visCount);
        current = Math.min(Math.max(current, 0), maxSlide);
        list.style.setProperty('transition', 'none', 'important');
        applyTransform(offsetForIndex(current));
        void list.offsetHeight;
        list.style.removeProperty('transition');
        updateDots(current);
      }
    }

    list.style.position = 'relative';
    requestAnimationFrame(function () { requestAnimationFrame(reset); });

    prevBtn.addEventListener('click', function () { goTo(current - 1); resetAutoplay(); });
    nextBtn.addEventListener('click', function () { goTo(current + 1); resetAutoplay(); });
    dotsEl.addEventListener('click', function (e) {
      var dot = e.target.closest('.llbny-tx-dot');
      if (dot) { goTo(parseInt(dot.dataset.index, 10)); resetAutoplay(); }
    });

    // ── Autoplay ──
    var autoplayTimer = null;

    function startAutoplay() {
      stopAutoplay();
      autoplayTimer = setInterval(function () { goTo(current + 1); }, 10000);
    }
    function stopAutoplay() { clearInterval(autoplayTimer); autoplayTimer = null; }
    function resetAutoplay() { startAutoplay(); }

    outer.addEventListener('mouseenter', stopAutoplay);
    outer.addEventListener('mouseleave', startAutoplay);
    startAutoplay();

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(reset, 150);
    });

    // ── Drag ──
    var isDragging = false;
    var dragStartX = 0;
    var dragStartTranslate = 0;
    var wasDragged = false;

    function readTranslateX() {
      var t = window.getComputedStyle(list).transform;
      if (!t || t === 'none') return 0;
      var m = t.match(/^matrix\((.+)\)$/);
      return m ? parseFloat(m[1].split(',')[4]) : 0;
    }

    list.addEventListener('mousedown', function (e) {
      if (window.innerWidth < 750 || e.button !== 0) return;
      e.preventDefault();
      isDragging = true;
      wasDragged = false;
      dragStartX = e.clientX;
      dragStartTranslate = readTranslateX();
      list.style.setProperty('transition', 'none', 'important');
      sliderGutter.classList.add('is-dragging');
      stopAutoplay();
    });

    document.addEventListener('mousemove', function (e) {
      if (!isDragging) return;
      var dx = e.clientX - dragStartX;
      if (Math.abs(dx) > 4) wasDragged = true;
      // No clamping — clones allow dragging past both ends
      list.style.setProperty('transform', 'translateX(' + (dragStartTranslate + dx) + 'px)', 'important');
    });

    document.addEventListener('mouseup', function (e) {
      if (!isDragging) return;
      isDragging = false;
      sliderGutter.classList.remove('is-dragging');
      list.style.removeProperty('transition');
      if (!wasDragged) return;

      var dx = e.clientX - dragStartX;
      var endOffset = -(dragStartTranslate + dx);
      var dom = allDOMItems();
      var vc = Math.ceil(getVisibleCount());
      var maxSlide = items.length - vc;
      var nearest = 0;
      var minDist = Infinity;
      for (var i = 0; i <= maxSlide; i++) {
        var dist = Math.abs(dom[i + cloneCount].offsetLeft - endOffset);
        if (dist < minDist) { minDist = dist; nearest = i; }
      }
      current = nearest;
      list.style.removeProperty('transform');
      goTo(current);
      resetAutoplay();
    });

    // Block card link clicks if the gesture was a drag
    list.addEventListener('click', function (e) {
      if (wasDragged) {
        e.preventDefault();
        e.stopPropagation();
        wasDragged = false;
      }
    }, true);
  }

  function initVideoPause() {
    const video = document.querySelector('.hero-video__player');
    if (!video) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.1 });
    observer.observe(video);
  }

  function initParallax() {
    // Subtle parallax on hero video — moves at 30% scroll speed
    const heroMedia = document.querySelector('.hero-video__media, .hero-video .banner__media');
    if (!heroMedia) return;
    heroMedia.style.willChange = 'transform';
    const onScroll = () => {
      heroMedia.style.transform = `translateY(${window.scrollY * 0.28}px)`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { initReveal(); initTransparentHeader(); initParallax(); initVideoPause(); initTreatmentsCarousel(); });
  } else {
    initReveal();
    initTransparentHeader();
    initParallax();
    initVideoPause();
    initTreatmentsCarousel();
  }
})();
